import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { parse, stringify } from 'query-string';

import { FormattedMessage } from '../../../util/reactIntl';
import { blockUserApi, unblockUserApi } from '../../../util/api';
import { fetchCurrentUser } from '../../../ducks/user.duck';

import { Modal, PrimaryButton } from '../../../components';
import BlockUserForm from './BlockUserForm';

import css from './BlockUser.module.css';

// No "block/no entry" icon exists yet under src/assets/icons (checked) - a small inline one
// here rather than adding a new asset file for a single one-off use, same call
// ReportListing.js's FlagIcon made.
const BlockIcon = props => (
  <svg
    className={props.className}
    width="18"
    height="18"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
    <line x1="3.5" y1="12.5" x2="12.5" y2="3.5" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

/**
 * Self-contained "Block user" feature for ProfilePage - the trigger button, the modal, the
 * form, and all of its own submit/loading/error state. Deliberately not wired into Redux
 * beyond the one fetchCurrentUser refresh on success - same reasoning as ReportListing.js:
 * this is UI-local state, and mirroring that component keeps both features consistent.
 *
 * Required by Apple App Review Guideline 1.2 (User-Generated Content): a mechanism to block
 * abusive users. Blocking (a) emails the admin inbox with the reason (see
 * server/api/block-user.js / blockUserEmailService.js) and (b) hides the blocked user's
 * listings from the blocker's search/browse feed instantly (see the blockedUserIds filter in
 * SearchPageWithGrid.js/SearchPageWithMap.js) - both read straight from the refreshed
 * currentUser this dispatches below, no extra round-trip needed.
 *
 * @param {Object} props
 * @param {string} props.profileUserId - uuid of the profile being viewed (the user to block)
 * @param {string} [props.profileUserDisplayName] - shown in the confirmation email/modal
 * @param {propTypes.currentUser} [props.currentUser] - the logged-in viewer, if any
 * @param {boolean} [props.blockedByOtherUser] - has the profile user already blocked the
 *   viewer? When true (and the viewer hasn't blocked them back), the trigger is suppressed -
 *   see TransactionPanel.js, the only current caller that knows this.
 */
const BlockUser = props => {
  const {
    profileUserId,
    profileUserDisplayName,
    currentUser,
    // Optional - only TransactionPanel.js currently knows this (it already checks it via
    // server/api/check-blocked-by.js for the messaging prompt). ProfilePage.js has no
    // transaction context to check it from, so it just doesn't pass this prop, and the
    // trigger button behaves exactly as it always did there.
    blockedByOtherUser = false,
  } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitInProgress, setSubmitInProgress] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [unblockInProgress, setUnblockInProgress] = useState(false);

  const isAuthenticated = !!currentUser?.id;
  const blockedUserIds = currentUser?.attributes?.profile?.privateData?.blockedUserIds || [];
  const isBlocked = isAuthenticated && blockedUserIds.includes(profileUserId);

  if (!profileUserId) {
    return null;
  }

  // Blocking someone who has already blocked you is a no-op you can't act on anyway (you
  // can't message them, and their listings hiding from your feed only happens once you
  // actually block them too - but showing an active "Block user" button here reads as if
  // there's still something to do, when the relationship is already severed from their
  // side). Only suppress this for the direction we don't already have a block of our own -
  // if isBlocked is also true, the "Blocked / Unblock" state below still needs to show.
  if (!isBlocked && blockedByOtherUser) {
    return null;
  }

  const refreshCurrentUser = () => dispatch(fetchCurrentUser({ enforce: true }));

  const handleBlockClick = () => {
    if (!isAuthenticated) {
      // Same "open the login modal on top of this page, come straight back" pattern as
      // ReportListing.js's handleReportClick.
      const currentUrl = `${location.pathname}${location.search || ''}${location.hash || ''}`;
      const { pathname, search, state } = location;
      const paramsObj = parse(search);
      paramsObj.login = 'open';
      paramsObj.redirect = encodeURIComponent(currentUrl);
      history.push(`${pathname}?${stringify(paramsObj)}`, state);
      return;
    }

    setSubmitError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSubmitError(null);
  };

  const handleSubmit = values => {
    setSubmitInProgress(true);
    setSubmitError(null);

    const { reason, otherReasonText, description } = values;

    blockUserApi({
      blockedUserId: profileUserId,
      blockedUserName: profileUserDisplayName,
      reason,
      otherReasonText: reason === 'other' ? otherReasonText : undefined,
      description,
    })
      .then(() => refreshCurrentUser())
      .then(() => {
        setSubmitInProgress(false);
        setIsModalOpen(false);
      })
      .catch(e => {
        setSubmitInProgress(false);
        setSubmitError(
          e?.error || e?.message || 'Something went wrong blocking this user. Please try again.'
        );
      });
  };

  const handleUnblock = () => {
    setUnblockInProgress(true);
    unblockUserApi({ blockedUserId: profileUserId })
      .then(() => refreshCurrentUser())
      .then(() => setUnblockInProgress(false))
      .catch(() => setUnblockInProgress(false));
  };

  return (
    <div className={css.root}>
      {isBlocked ? (
        <div className={css.blockedRow}>
          {/* No icon here (there used to be one) - at the pill's small size the "no entry"
              circle+line just read as a plain dot, not a recognizable block symbol, which is
              what prompted this. The red pill + text alone says "blocked" unambiguously. */}
          <span className={css.blockedLabel}>
            <FormattedMessage id="BlockUser.blockedLabel" />
          </span>
          <button
            type="button"
            className={css.unblockButton}
            onClick={handleUnblock}
            disabled={unblockInProgress}
          >
            <FormattedMessage id="BlockUser.unblockButton" />
          </button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          className={css.blockButton}
          onClick={handleBlockClick}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleBlockClick();
            }
          }}
        >
          <BlockIcon className={css.blockIcon} />
          <FormattedMessage id="BlockUser.blockButton" />
        </div>
      )}

      <Modal
        id="ProfilePage.blockUser"
        contentClassName={css.modalContent}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        usePortal
      >
        <h2 className={css.modalHeading}>
          <FormattedMessage id="BlockUser.modalHeading" values={{ name: profileUserDisplayName }} />
        </h2>
        <BlockUserForm onSubmit={handleSubmit} onCancel={handleCloseModal} inProgress={submitInProgress} serverError={submitError} />
      </Modal>
    </div>
  );
};

export default BlockUser;
